/**
 * TrainingReview — Tank Redesign per-face training queue.
 *
 * Source: design_handoff_tank/README.md § "09 — Manage / Training" +
 * tank-redesign/screens-rest.jsx § Screen09Training. Reviews one face
 * at a time: source photo + face-box overlays on the left, detected
 * face card + suggestions list + Skip / Reject / Confirm footer on
 * the right, KPI strip at the bottom.
 *
 * Wraps the existing training API helpers (fetchTrainingImages,
 * fetchTrainingFaces, assignPerson, ignoreFace, markTrainingDone,
 * fetchTrainingStats) — no new backend.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  fetchTrainingImages,
  fetchTrainingFaces,
  fetchTrainingStats,
  assignPerson,
  ignoreFace,
  markTrainingDone,
  searchPersons,
  uploadTrainingImages,
} from '../api';
import { Eyebrow, Mono, Btn, Kbd, StatusPill } from '../shared/components/redesign';
import { useSetTopBar } from '../layouts/ShellContext';
import {
  CheckIcon,
  XMarkIcon,
  ForwardIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import styles from './TrainingReview.module.css';
import AddPersonModal from './AddPersonModal';

const FACE_STATE = {
  CONFIRMED: 'confirmed',
  ACTIVE: 'active',
  PENDING: 'pending',
  IGNORED: 'ignored',
};

function getFaceState(face, activeId) {
  if (face.id === activeId) return FACE_STATE.ACTIVE;
  if (face.ignored) return FACE_STATE.IGNORED;
  if (face.person) return FACE_STATE.CONFIRMED;
  return FACE_STATE.PENDING;
}

export default function TrainingReview() {
  const [images, setImages] = useState([]);
  const [faces, setFaces] = useState([]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeFaceId, setActiveFaceId] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const cancelRef = useRef(false);
  const photoFrameRef = useRef(null);
  const photoImgRef = useRef(null);
  const filmstripScrollRef = useRef(null);
  const [imgBounds, setImgBounds] = useState(null);
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [addPersonInitialQuery, setAddPersonInitialQuery] = useState('');

  // Refresh queue + stats after an upload (or any other op that adds images).
  const refreshQueue = useCallback(async () => {
    const [imgs, st] = await Promise.all([
      fetchTrainingImages('ready'),
      fetchTrainingStats().catch(() => null),
    ]);
    setImages(imgs?.results || imgs || []);
    setStats(st || null);
    setActiveImageIndex(0);
  }, []);

  // Top-bar wiring (hybrid chrome principle #1).
  const setTopBar = useSetTopBar();
  useEffect(() => {
    setTopBar({
      breadcrumb: '§ MANAGE · Training · Review',
      right: (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 12,
            color: 'var(--rd-color-text-faint)',
            fontFamily: 'var(--rd-font-mono)',
            fontSize: 'var(--rd-type-eyebrow)',
            letterSpacing: 'var(--rd-track-eyebrow)',
            textTransform: 'uppercase',
          }}
        >
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <Kbd>↵</Kbd>
            confirm
          </span>
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <Kbd>X</Kbd>
            reject
          </span>
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <Kbd>S</Kbd>
            skip
          </span>
        </span>
      ),
    });
    return () => setTopBar(null);
  }, [setTopBar]);

  // Common upload handler — accepts File[] from drop or input change.
  const handleUploadFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []).filter((f) =>
        f.type?.startsWith('image/'),
      );
      if (files.length === 0) return;
      setUploadError(null);
      setUploading(true);
      try {
        await uploadTrainingImages(files);
        await refreshQueue();
      } catch (err) {
        setUploadError(
          err?.response?.data?.detail || err?.message || 'Upload failed',
        );
      } finally {
        setUploading(false);
      }
    },
    [refreshQueue],
  );

  // Initial load
  useEffect(() => {
    cancelRef.current = false;
    void (async () => {
      setLoading(true);
      try {
        const [imgs, st] = await Promise.all([
          fetchTrainingImages('ready'),
          fetchTrainingStats().catch(() => null),
        ]);
        if (cancelRef.current) return;
        setImages(imgs?.results || imgs || []);
        setStats(st || null);
      } finally {
        if (!cancelRef.current) setLoading(false);
      }
    })();
    return () => {
      cancelRef.current = true;
    };
  }, []);

  // Load faces for the active image
  const activeImage = images[activeImageIndex] || null;
  useEffect(() => {
    if (!activeImage) return;
    void (async () => {
      const data = await fetchTrainingFaces(activeImage.id);
      const list = data?.results || data || [];
      setFaces(list);
      // Pick the first pending face as active.
      const firstPending = list.find((f) => !f.person && !f.ignored);
      setActiveFaceId(firstPending?.id || list[0]?.id || null);
    })();
  }, [activeImage?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Active face index (1-based for display)
  const activeFace = useMemo(() => faces.find((f) => f.id === activeFaceId) || null, [faces, activeFaceId]);
  const activeFaceIndex = useMemo(
    () => faces.findIndex((f) => f.id === activeFaceId),
    [faces, activeFaceId],
  );

  // Face crop for the Detected Face card: position the full preview inside a
  // 96×116 overflow:hidden frame so the active bbox lands centered.
  const activeFaceCropStyle = useMemo(() => {
    if (!activeFace || !activeImage?.preview_url) return null;
    const bbox = activeFace.bbox || activeFace.frame_bbox;
    const W = activeImage.width;
    const H = activeImage.height;
    if (!bbox || bbox.length !== 4 || !W || !H) return null;
    const [x1, y1, x2, y2] = bbox;
    const faceW = x2 - x1;
    const faceH = y2 - y1;
    if (faceW <= 0 || faceH <= 0) return null;
    const containerW = 96;
    const containerH = 116;
    const padding = 1.4;
    const scale = Math.min(containerW / (faceW * padding), containerH / (faceH * padding));
    return {
      width: W * scale,
      height: H * scale,
      position: 'absolute',
      left: containerW / 2 - (x1 + faceW / 2) * scale,
      top: containerH / 2 - (y1 + faceH / 2) * scale,
      maxWidth: 'none',
    };
  }, [activeFace, activeImage]);

  // Measure the actually-rendered image rect inside .photoFrame. Source image
  // uses object-fit: contain so it letterboxes whenever the aspect ratios
  // disagree — overlay rects must be placed in pixel space derived from the
  // rendered image, not percent of the container. Mirrors usePhotoViewer.js.
  useEffect(() => {
    const frame = photoFrameRef.current;
    const W = activeImage?.width;
    const H = activeImage?.height;
    if (!frame || !W || !H) {
      setImgBounds(null);
      return undefined;
    }
    const update = () => {
      const cW = frame.clientWidth;
      const cH = frame.clientHeight;
      if (!cW || !cH) return;
      const imageAspect = W / H;
      const containerAspect = cW / cH;
      let w;
      let h;
      let x;
      let y;
      if (imageAspect > containerAspect) {
        w = cW;
        h = cW / imageAspect;
        x = 0;
        y = (cH - h) / 2;
      } else {
        h = cH;
        w = cH * imageAspect;
        x = (cW - w) / 2;
        y = 0;
      }
      setImgBounds({ x, y, w, h });
    };
    update();
    const img = photoImgRef.current;
    if (img) {
      img.addEventListener('load', update);
      if (img.complete) update();
    }
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(frame);
    }
    return () => {
      if (img) img.removeEventListener('load', update);
      if (ro) ro.disconnect();
    };
  }, [activeImage?.id, activeImage?.width, activeImage?.height, activeImage?.preview_url]);

  // Auto-scroll the active filmstrip tile into view.
  useEffect(() => {
    const scroller = filmstripScrollRef.current;
    if (!scroller) return;
    const node = scroller.querySelector(`[data-filmstrip-index="${activeImageIndex}"]`);
    node?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeImageIndex, images.length]);

  const scrollFilmstrip = useCallback((dx) => {
    filmstripScrollRef.current?.scrollBy({ left: dx, behavior: 'smooth' });
  }, []);

  const advance = useCallback(() => {
    // Move to the next pending face in the same image, or to the next image.
    const remaining = faces.filter((f) => !f.person && !f.ignored && f.id !== activeFaceId);
    if (remaining.length > 0) {
      setActiveFaceId(remaining[0].id);
      return;
    }
    // No more pending in this image — mark image done and step to the next.
    if (activeImage) {
      void markTrainingDone(activeImage.id).catch(() => {});
    }
    if (activeImageIndex + 1 < images.length) {
      setActiveImageIndex((i) => i + 1);
    } else {
      // Out of images — refresh.
      void fetchTrainingImages('ready').then((imgs) => {
        setImages(imgs?.results || imgs || []);
        setActiveImageIndex(0);
      });
    }
  }, [faces, activeFaceId, activeImage, activeImageIndex, images.length]);

  const handleConfirm = useCallback(async (personId) => {
    if (!activeFace || !personId || saving) return;
    setSaving(true);
    try {
      await assignPerson(activeFace.id, personId, null);
      // Optimistic: mark this face confirmed locally so the next face flips.
      setFaces((prev) =>
        prev.map((f) => (f.id === activeFace.id ? { ...f, person: personId } : f)),
      );
      advance();
      setSearchQuery('');
      setSearchResults([]);
    } finally {
      setSaving(false);
    }
  }, [activeFace, advance, saving]);

  // Open the AddPersonModal pre-filled from the current search query (if any)
  // so the user can set prefix/suffix/jersey/team before the Person is
  // created and assigned to the active face.
  const openAddPersonModal = useCallback((prefill = '') => {
    if (!activeFace) return;
    setAddPersonInitialQuery(prefill);
    setAddPersonOpen(true);
  }, [activeFace]);

  const handlePersonSaved = useCallback(({ personId, displayName }) => {
    setFaces((prev) =>
      prev.map((f) =>
        f.id === activeFace?.id
          ? { ...f, person: personId, person_name: displayName }
          : f,
      ),
    );
    setSearchQuery('');
    setSearchResults([]);
    setAddPersonOpen(false);
    setAddPersonInitialQuery('');
    advance();
  }, [activeFace, advance]);

  const handleReject = useCallback(async () => {
    if (!activeFace || saving) return;
    setSaving(true);
    try {
      await ignoreFace(activeFace.id);
      setFaces((prev) =>
        prev.map((f) => (f.id === activeFace.id ? { ...f, ignored: true } : f)),
      );
      advance();
    } finally {
      setSaving(false);
    }
  }, [activeFace, advance, saving]);

  const handleSkip = useCallback(() => {
    advance();
  }, [advance]);

  // Person search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const results = await searchPersons(searchQuery);
        setSearchResults(results?.results || results || []);
      } catch {
        setSearchResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Keyboard shortcuts: Enter / X / S
  useEffect(() => {
    const handler = (e) => {
      if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') return;
      if (e.key === 'Enter') {
        // Confirm with the top suggestion if any
        const top = activeFace?.suggestions?.[0]?.person_id || searchResults[0]?.id;
        if (top) handleConfirm(top);
      } else if (e.key === 'x' || e.key === 'X') {
        handleReject();
      } else if (e.key === 's' || e.key === 'S') {
        handleSkip();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeFace, searchResults, handleConfirm, handleReject, handleSkip]);

  const totalFaces = faces.length;
  const pendingCount = faces.filter((f) => !f.person && !f.ignored).length;

  return (
    <div className={styles.screen}>
      {/* Top eyebrow */}
      <div className={styles.topbar}>
        <Eyebrow>
          {activeImage?.id ? `Face ${activeFaceIndex + 1} / ${totalFaces}` : 'Training queue'}
        </Eyebrow>
        <span className={styles.divider}>·</span>
        <Mono className={styles.fileId}>{activeImage?.filename || activeImage?.id?.slice(0, 8) || '—'}</Mono>
        <div className={styles.spacer} />
        <Mono className={styles.queueCounter}>
          Image {Math.min(activeImageIndex + 1, images.length || 1)} / {images.length || 0}
        </Mono>
      </div>

      <div className={styles.body}>
        {/* Left pane — source photo + face overlay */}
        <div className={styles.leftPane}>
          <div className={styles.photoFrame} ref={photoFrameRef}>
            {activeImage?.preview_url ? (
              <img
                ref={photoImgRef}
                src={activeImage.preview_url}
                alt=""
                className={styles.photoImg}
              />
            ) : (
              <div className={styles.photoPlaceholder}>
                <Mono>No image</Mono>
              </div>
            )}
            {/* Face overlays — positioned in pixel space relative to the
                rendered image rect (imgBounds), since the source <img> uses
                object-fit: contain and letterboxes inside .photoFrame. */}
            {imgBounds && faces.map((f, i) => {
              const state = getFaceState(f, activeFaceId);
              const bbox = f.bbox || f.frame_bbox;
              if (!bbox || !activeImage?.width || !activeImage?.height) return null;
              const [x1, y1, x2, y2] = bbox;
              const W = activeImage.width;
              const H = activeImage.height;
              const left = imgBounds.x + (x1 / W) * imgBounds.w;
              const top = imgBounds.y + (y1 / H) * imgBounds.h;
              const width = ((x2 - x1) / W) * imgBounds.w;
              const height = ((y2 - y1) / H) * imgBounds.h;
              return (
                <div
                  key={f.id}
                  className={`${styles.faceBox} ${styles[`faceBox-${state}`]}`}
                  style={{ left, top, width, height }}
                  onClick={() => state !== FACE_STATE.IGNORED && setActiveFaceId(f.id)}
                >
                  <span className={styles.faceTag}>
                    {state === FACE_STATE.ACTIVE && `Face ${String(i + 1).padStart(2, '0')} · TRAINING`}
                    {state === FACE_STATE.CONFIRMED && `✓ ${f.person_name || 'confirmed'}`}
                    {state === FACE_STATE.PENDING && `Face ${String(i + 1).padStart(2, '0')} · pending`}
                    {state === FACE_STATE.IGNORED && `Face ${String(i + 1).padStart(2, '0')} · ignored`}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Mini queue filmstrip — renders the full queue with click-to-jump
              navigation. The active tile is auto-scrolled into view. */}
          <div className={styles.filmstrip}>
            <Mono className={styles.filmstripLabel}>Queue</Mono>
            <button
              type="button"
              className={styles.filmstripChevron}
              onClick={() => scrollFilmstrip(-280)}
              title="Scroll left"
              aria-label="Scroll filmstrip left"
            >
              <ChevronLeftIcon style={{ width: 14, height: 14 }} />
            </button>
            <div className={styles.filmstripScroll} ref={filmstripScrollRef}>
              {images.map((img, i) => (
                <div
                  key={img.id}
                  data-filmstrip-index={i}
                  className={`${styles.filmstripTile} ${i === activeImageIndex ? styles.filmstripTileActive : ''}`}
                  onClick={() => setActiveImageIndex(i)}
                  title={img.filename || img.id}
                >
                  {img.preview_url ? (
                    <img src={img.preview_url} alt="" loading="lazy" />
                  ) : (
                    <div className={styles.filmstripPlaceholder} />
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              className={styles.filmstripChevron}
              onClick={() => scrollFilmstrip(280)}
              title="Scroll right"
              aria-label="Scroll filmstrip right"
            >
              <ChevronRightIcon style={{ width: 14, height: 14 }} />
            </button>
          </div>
        </div>

        {/* Right pane — face card + suggestions + actions */}
        <div className={styles.rightPane}>
          <Eyebrow className={styles.rightEyebrow}>Detected face</Eyebrow>

          <div className={styles.faceCard}>
            {activeFaceCropStyle ? (
              <div className={styles.faceCardImgFrame}>
                <img
                  src={activeImage.preview_url}
                  alt=""
                  style={activeFaceCropStyle}
                  draggable={false}
                />
              </div>
            ) : (
              <div className={styles.faceCardPlaceholder}>
                <Mono>No face</Mono>
              </div>
            )}
            <div className={styles.faceCardMeta}>
              <Mono className={styles.faceCardId}>face {activeFace?.id?.slice(0, 6) || '—'}</Mono>
              {activeFace?.score != null && (
                <span
                  className={styles.confidenceBadge}
                  title="How confident the detector is that this bbox contains a face. Independent of who the face belongs to — see Suggestions for person-match similarity."
                >
                  {(activeFace.score * 100).toFixed(0)}% face detection
                </span>
              )}
            </div>
          </div>

          <Eyebrow className={styles.rightEyebrow}>Suggestions</Eyebrow>
          {activeFace?.suggestions?.length ? (
            <ul className={styles.suggestList}>
              {activeFace.suggestions.slice(0, 3).map((s, i) => {
                const similarity = s.distance != null
                  ? Math.max(0, Math.min(100, (1 - s.distance) * 100))
                  : null;
                return (
                  <li key={s.person_id} className={i === 0 ? styles.suggestRowTop : styles.suggestRow}>
                    <span className={styles.suggestName}>{s.person_name}</span>
                    <span className={styles.suggestBar}>
                      <span
                        className={styles.suggestBarFill}
                        style={{ width: `${similarity != null ? Math.max(5, similarity) : 0}%` }}
                      />
                    </span>
                    <Mono className={styles.suggestPct}>
                      {similarity != null ? `${Math.round(similarity)}%` : '—'}
                    </Mono>
                    <Btn kind={i === 0 ? 'primary' : 'ghost'} size="sm" onClick={() => handleConfirm(s.person_id)}>
                      Pick
                    </Btn>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className={styles.suggestEmpty}>No suggestions yet.</p>
          )}

          <div className={styles.searchHead}>
            <Eyebrow className={styles.rightEyebrow}>Search persons</Eyebrow>
            <button
              type="button"
              className={styles.newPersonLink}
              onClick={() => openAddPersonModal('')}
              disabled={!activeFace}
              title="Add a new person"
            >
              + New person
            </button>
          </div>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Type a name…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchResults.length > 0 && (
            <ul className={styles.searchList}>
              {searchResults.slice(0, 5).map((p) => (
                <li key={p.id} className={styles.searchRow}>
                  <span>{p.display_name || `${p.first_name} ${p.last_name}`.trim()}</span>
                  <Btn kind="ghost" size="sm" onClick={() => handleConfirm(p.id)}>
                    Confirm
                  </Btn>
                </li>
              ))}
            </ul>
          )}
          {searchQuery.trim() && searchResults.length === 0 && activeFace && (
            <button
              type="button"
              className={styles.createPersonBtn}
              onClick={() => openAddPersonModal(searchQuery)}
              disabled={saving}
            >
              + Create &ldquo;{searchQuery.trim()}&rdquo; as new person
            </button>
          )}

          {/* Footer actions */}
          <div className={styles.footer}>
            <Btn
              kind="ghost"
              size="sm"
              icon={<ForwardIcon style={{ width: 12, height: 12 }} />}
              onClick={handleSkip}
              disabled={saving}
            >
              Skip <Kbd>S</Kbd>
            </Btn>
            <Btn
              kind="danger"
              size="sm"
              icon={<XMarkIcon style={{ width: 12, height: 12 }} />}
              onClick={handleReject}
              disabled={saving || !activeFace}
            >
              Reject <Kbd>X</Kbd>
            </Btn>
            <div className={styles.footerSpacer} />
            <Btn
              kind="primary"
              size="sm"
              icon={<CheckIcon style={{ width: 12, height: 12 }} />}
              disabled={saving || !activeFace || !(activeFace?.suggestions?.[0]?.person_id || searchResults[0]?.id)}
              onClick={() =>
                handleConfirm(activeFace?.suggestions?.[0]?.person_id || searchResults[0]?.id)
              }
            >
              Confirm <Kbd>↵</Kbd>
            </Btn>
          </div>
        </div>
      </div>

      {/* Bottom KPI strip */}
      <div className={styles.kpiStrip}>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Persons</span>
          <Mono className={styles.kpiValue}>{stats?.persons ?? '—'}</Mono>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Trained</span>
          <Mono className={styles.kpiValue}>{stats?.by_status?.done ?? '—'}</Mono>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Labeled faces</span>
          <Mono className={styles.kpiValue}>
            {stats?.faces?.labeled ?? '—'}/{stats?.faces?.total ?? '—'}
          </Mono>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Queue</span>
          <Mono className={styles.kpiValue}>{stats?.by_status?.ready ?? '—'}</Mono>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>This image</span>
          <Mono className={styles.kpiValue}>
            {pendingCount}/{totalFaces}
          </Mono>
        </div>
        <div className={styles.kpiSpacer} />
        <Btn kind="soft" size="sm" icon={<ArrowPathIcon style={{ width: 12, height: 12 }} />}>
          Train now
        </Btn>
      </div>

      {loading && <div className={styles.loadingOverlay}><Mono>Loading…</Mono></div>}
      {!loading && images.length === 0 && (
        <div
          className={`${styles.emptyOverlay} ${styles.uploadZone} ${
            dragOver ? styles.uploadZoneActive : ''
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            if (!dragOver) setDragOver(true);
          }}
          onDragLeave={(e) => {
            // Only clear when leaving the zone itself, not children.
            if (e.currentTarget.contains(e.relatedTarget)) return;
            setDragOver(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleUploadFiles(e.dataTransfer?.files);
          }}
        >
          <Mono>No images pending review.</Mono>
          <span style={{ marginTop: 6 }}>
            <StatusPill kind="done">All caught up</StatusPill>
          </span>
          <div className={styles.uploadHint}>
            <Mono className={styles.uploadHintLabel}>
              Drag images here to add to the training queue
            </Mono>
            <span className={styles.uploadHintOr}>or</span>
            <Btn
              kind="primary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading…' : 'Choose files'}
            </Btn>
          </div>
          {uploadError && (
            <div className={styles.uploadError}>
              <Mono>Upload failed:</Mono> {uploadError}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              handleUploadFiles(e.target.files);
              // Reset so re-selecting the same file re-fires onChange.
              e.target.value = '';
            }}
          />
        </div>
      )}
      {/* Persistent "+ Upload" affordance on the queue header so the user can
          add to the queue even when faces are pending. */}
      {!loading && images.length > 0 && (
        <button
          type="button"
          className={styles.uploadFab}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          title="Add images to training queue"
        >
          {uploading ? 'Uploading…' : '+ Upload'}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              handleUploadFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </button>
      )}

      <AddPersonModal
        isOpen={addPersonOpen}
        initialQuery={addPersonInitialQuery}
        activeFace={activeFace}
        onCancel={() => setAddPersonOpen(false)}
        onSaved={handlePersonSaved}
      />
    </div>
  );
}
